// src/app/features/slices/promotionSlice.js

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import apiClient from "../../../api/apiClient";
import ENDPOINTS from "../../../api/endPoint";
import { fetchAllCustomers } from "../../../services/customerService";
import { fetchPromotionFiles } from "../../../services/promotionService";

// ==========================
// FETCH ALL CUSTOMERS SLICE
// ==========================

// Fetch all customers for promotion
export const fetchCustomersAsync = createAsyncThunk(
  "promotiondata/fetchCustomers",
  async (_, { rejectWithValue }) => {
    try {
      const customers = await fetchAllCustomers();
      return customers;
    } catch (error) {
      console.error("Error fetching customers:", error);
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// ✅ Thunk: Send Promotion Emails
export const sendPromotionEmailThunk = createAsyncThunk(
  "promotion/sendEmail",
  async ({ file, customers, connectionId }, { rejectWithValue }) => {
    try {
      
      const formData = new FormData();
      formData.append("file", file);
      // Append each customer ID individually so ASP.NET Core binds them to a List<int>
      customers.forEach(id => {
          formData.append("CustomerIds", id);
      });
      if (connectionId) {
          formData.append("ConnectionId", connectionId);
      }

      const response = await apiClient(ENDPOINTS.PROMOTION.SEND_EMAIL, {
        method: "POST",
        body: formData,
        requiresAuth: true,
        suppressToast: true,
      });

      return response;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to send promotion emails.";
      return rejectWithValue(message);
    }
  }
);

export const getPromotionFilesThunk = createAsyncThunk(
  "promotion/fetchAllFiles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchPromotionFiles();
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// ==========================
// INITIAL STATE
// ==========================

const initialPromotionState = {
  customers: [],
  loading: false, // for fetchCustomers
  isLoading: false, // for sendPromotion
  uploadProgress: 0,
  uploadStatus: "",
  uploadResults: null,
  error: null,
};

// ==========================
// SLICE
// ==========================

const promotionSlice = createSlice({
  name: "promotiondata",
  initialState: initialPromotionState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ---- Fetch all customers ----
      .addCase(fetchCustomersAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomersAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = action.payload || [];
      })
      .addCase(fetchCustomersAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ---- Send Promotion Emails ----
      .addCase(sendPromotionEmailThunk.pending, (state) => {
        state.isLoading = true;
        state.uploadProgress = 0;
        state.uploadStatus = "Sending promotion emails...";
        state.error = null;
      })
      .addCase(sendPromotionEmailThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.uploadProgress = 100;
        state.uploadStatus = "Promotion emails sent successfully!";
        state.uploadResults = action.payload;
      })
      .addCase(sendPromotionEmailThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.uploadProgress = 0;
        state.uploadStatus = "Error sending promotions.";
        state.error = action.payload;
      })

     .addCase(getPromotionFilesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPromotionFilesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.promotionFiles = action.payload;
      })
      .addCase(getPromotionFilesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
     

  },
});

export default promotionSlice.reducer;
